import { get } from 'lodash';
import { observer } from 'mobx-react';
import { ORDER_TYPES } from '@nandosaus/constants';
import { PropTypes } from 'prop-types';
import { restaurantPropTypes } from '@nandosaus/prop-types';
import { useRootStore } from '@nandosaus/state-management';
import React, { useEffect, useMemo } from 'react';

import { Alert } from '@components/alert';
import { Box, Flex } from '@components/grid';

import { LocationLink } from '@components/order-settings-modal/location-link';
import { OrderTypePicker } from '@components/order-settings-modal-catering/order-type-picker';
import { SchedulePickUp } from '../schedule-pick-up';

const OrderSettingsScreen = ({
  orderType,
  restaurant,
  showRestaurantFinder,
  showDeliveryAddressFinder,
  updateSetting,
  settings,
  validationErrors,
}) => {
  const { OrderingContextStore, DeliveryStore } = useRootStore();

  const { availableOrderTypes, fieldMapFor } = OrderingContextStore;
  const fields = useMemo(() => fieldMapFor(orderType), [fieldMapFor, orderType]);

  const isCateringDelivery = orderType === ORDER_TYPES.CATERING_DELIVERY;
  // Catering delivery's restaurant is the nearest eligible store, derived from the
  // delivery address; catering pickup uses the user-selected restaurant.
  const effectiveRestaurant = isCateringDelivery ? DeliveryStore.deliveryRestaurant : restaurant;

  // Catering delivery resolves its restaurant — and therefore the schedule (date/time) — from a
  // delivery eligibility estimate. A pre-filled/restored address doesn't fetch one on its own:
  // setDeliveryAddress only estimates when the address is actively (re)set, and the cart-driven
  // estimate reaction needs cart items (the cart is empty at this stage). Without this the schedule
  // never appears for an existing address. Fetch the estimate once when we have an address but no
  // resolved delivery restaurant and deliverability hasn't been determined yet.
  useEffect(() => {
    if (
      isCateringDelivery &&
      DeliveryStore.deliveryAddress &&
      !DeliveryStore.deliveryRestaurant &&
      !DeliveryStore.loading &&
      !DeliveryStore.deliverabilityStatus
    ) {
      DeliveryStore.getDeliveryEstimate();
    }
  }, [
    isCateringDelivery,
    DeliveryStore,
    DeliveryStore.deliveryAddress,
    DeliveryStore.deliveryRestaurant,
    DeliveryStore.loading,
    DeliveryStore.deliverabilityStatus,
  ]);

  const formattedStatusMessage = effectiveRestaurant?.getFormattedStatusMessage({ orderType });
  const restaurantDisabled = formattedStatusMessage?.disableOptions || false;

  // The scheduled date/time picker is shown once we have a restaurant resolved
  // (selected for pickup, address-derived for delivery).
  const showSchedule = effectiveRestaurant && get(effectiveRestaurant, 'cateringEnabled') && !restaurantDisabled;

  return (
    <Flex flexDirection="column" position="relative" mt="40px">
      <Box position="static" zIndex={1}>
        <OrderTypePicker
          errorMessage={validationErrors.orderType}
          orderType={orderType}
          availableOrderTypes={availableOrderTypes}
          onSelect={value => updateSetting('orderType', value)}
        />
        {orderType && (
          <Box gap="8px" mb="30px" display="flex" flexDirection="column">
            <LocationLink
              deliveryAddress={DeliveryStore.deliveryAddress}
              deliveryNotes={DeliveryStore.deliveryNotes}
              orderType={orderType}
              restaurant={effectiveRestaurant}
              showDeliveryAddressFinder={showDeliveryAddressFinder}
              showRestaurantFinder={fields.restaurantId?.writable ? showRestaurantFinder : undefined}
            />
            {validationErrors.restaurant !== undefined && <Alert type="error">{validationErrors.restaurant}</Alert>}
            {validationErrors.deliveryAddress !== undefined && (
              <Alert type="error">{validationErrors.deliveryAddress}</Alert>
            )}
          </Box>
        )}
        {showSchedule && (
          <SchedulePickUp
            restaurant={effectiveRestaurant}
            orderType={orderType}
            updateSetting={updateSetting}
            settings={settings}
          />
        )}
      </Box>
    </Flex>
  );
};

OrderSettingsScreen.propTypes = {
  showRestaurantFinder: PropTypes.func.isRequired,
  showDeliveryAddressFinder: PropTypes.func.isRequired,
  updateSetting: PropTypes.func.isRequired,
  orderType: PropTypes.oneOf(Object.values(ORDER_TYPES)),
  restaurant: restaurantPropTypes,
  settings: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  validationErrors: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

OrderSettingsScreen.defaultProps = {
  orderType: undefined,
  restaurant: undefined,
  settings: {},
};

const ComposedOrderSettingScreen = observer(OrderSettingsScreen);
export { ComposedOrderSettingScreen as OrderSettingsScreen };
